"use client";

import { useState } from "react";
import { CaptchaWidget } from "@/components/auth/CaptchaWidget";

/**
 * Public data-subject request form (LGPD Art. 18). Posts to
 * /api/lgpd/data-request, which creates a `pending_confirm` request and
 * e-mails a confirmation link (double opt-in).
 */
export function SolicitarDadosForm({ slaDays }: { slaDays: number }) {
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [error, setError] = useState("");
  const [captchaReady, setCaptchaReady] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setError("");

    const fd = new FormData(e.currentTarget);
    const payload = {
      type: String(fd.get("type") ?? ""),
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      cpfCnpj: String(fd.get("cpfCnpj") ?? ""),
      orderRef: String(fd.get("orderRef") ?? ""),
      message: String(fd.get("message") ?? ""),
      website: String(fd.get("website") ?? ""),
      recaptchaToken: String(fd.get("recaptchaToken") ?? ""),
      powChallenge: String(fd.get("powChallenge") ?? ""),
      powNonce: String(fd.get("powNonce") ?? ""),
    };

    try {
      const res = await fetch("/api/lgpd/data-request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error || "Não foi possível registrar a solicitação.");
      }
      setStatus("ok");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro inesperado.");
      setStatus("error");
    }
  }

  if (status === "ok") {
    return (
      <div className="sd-feedback sd-feedback--ok" role="status">
        <strong>Solicitação registrada.</strong>
        <p style={{ margin: "6px 0 0" }}>
          Enviamos um e-mail de confirmação. Abra o link recebido para darmos andamento — sua
          solicitação só é processada após a confirmação. Prazo de resposta: até {slaDays} dias úteis
          (LGPD, Art. 18).
        </p>
      </div>
    );
  }

  return (
    <form className="sd-form" onSubmit={handleSubmit} noValidate>
      <label>
        Tipo de solicitação
        <select name="type" required defaultValue="export">
          <option value="export">Exportar meus dados pessoais</option>
          <option value="delete">Excluir meus dados pessoais</option>
        </select>
      </label>

      <label>
        Nome completo
        <input type="text" name="name" required minLength={2} maxLength={160} autoComplete="name" />
      </label>

      <label>
        E-mail
        <input type="email" name="email" required maxLength={160} autoComplete="email" />
      </label>

      <label>
        CPF/CNPJ <span className="sd-note">(opcional — ajuda a localizar seus registros)</span>
        <input type="text" name="cpfCnpj" maxLength={40} />
      </label>

      <label>
        Número de pedido/contrato <span className="sd-note">(opcional)</span>
        <input type="text" name="orderRef" maxLength={80} />
      </label>

      <label>
        Mensagem adicional <span className="sd-note">(opcional)</span>
        <textarea name="message" rows={4} maxLength={4000} />
      </label>

      {/* honeypot */}
      <input type="text" name="website" className="sd-hp" tabIndex={-1} autoComplete="off" aria-hidden />

      <p className="sd-note">
        Alguns dados podem ser retidos para cumprimento de obrigações legais. A exclusão de dados
        pode afetar serviços ou pedidos em andamento.
      </p>

      <CaptchaWidget flow="form" variant="dark" onReady={setCaptchaReady} />

      {status === "error" ? (
        <div className="sd-feedback sd-feedback--err" role="alert">
          {error}
        </div>
      ) : null}

      <div>
        <button type="submit" className="btn btn--primary" disabled={status === "sending" || !captchaReady}>
          {status === "sending" ? "Enviando…" : "Enviar solicitação"}
        </button>
      </div>
    </form>
  );
}
